// src/pages/Statistics.jsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  alpha,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useStateContext } from "contexts/userContext";
import { useTranslation } from "react-i18next";
import { baseURL } from "constants/api";
import axios from "axios";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import PeopleIcon from "@mui/icons-material/People";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentIcon from "@mui/icons-material/Payment";

export function Statistics() {
  const { isAuthenticated } = useAuth0();
  const { restaurant_id, settings } = useStateContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const [orders, setOrders] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("all"); // all, today, week, month

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!restaurant_id) return;
      
      try {
        setLoading(true);
        const response = await axios.get(`${baseURL}/orders/${restaurant_id}`);
        setOrders(response.data || []);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [restaurant_id]);

  // Fetch dishes
  useEffect(() => {
    const fetchDishes = async () => {
      if (!restaurant_id) return;
      
      try {
        const response = await axios.get(`${baseURL}/dishes/${restaurant_id}`);
        setDishes(response.data || []);
      } catch (error) {
        console.error("Error fetching dishes:", error);
      }
    };

    fetchDishes();
  }, [restaurant_id]);

  // Filter orders by time range
  const filteredOrders = useMemo(() => {
    if (!orders.length) return [];
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    return orders.filter(order => {
      if (!order.order_date) return false;
      const orderDate = new Date(order.order_date);
      
      switch (timeRange) {
        case "today":
          return orderDate >= today;
        case "week":
          return orderDate >= weekAgo;
        case "month":
          return orderDate >= monthAgo;
        default:
          return true;
      }
    });
  }, [orders, timeRange]);

  // Calculate statistics
  const stats = useMemo(() => {
    const validOrders = filteredOrders.filter(order => 
      order.totalPrice && 
      order.cartItems && 
      order.totalPrice !== null
    );

    // Total revenue
    const totalRevenue = validOrders.reduce((sum, order) => {
      return sum + (parseFloat(order.totalPrice) || 0);
    }, 0);

    // Total orders
    const totalOrders = validOrders.length;

    // Average order value
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Parse cart items and count dishes
    const dishSales = {};
    const dishRevenue = {};
    let totalItems = 0;

    validOrders.forEach(order => {
      try {
        const cartItems = JSON.parse(order.cartItems);
        cartItems.forEach(item => {
          totalItems += item.quantity || 0;
          const dishName = item.title || "Unknown";
          dishSales[dishName] = (dishSales[dishName] || 0) + (item.quantity || 0);
          dishRevenue[dishName] = (dishRevenue[dishName] || 0) + 
            ((item.price || 0) * (item.quantity || 0));
        });
      } catch (error) {
        console.error("Error parsing cart items:", error);
      }
    });

    // Most popular dishes
    const popularDishes = Object.entries(dishSales)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([name, quantity]) => ({
        name,
        quantity,
        revenue: dishRevenue[name] || 0,
      }));

    // Delivery options stats
    const deliveryStats = {};
    validOrders.forEach(order => {
      const option = order.optionDelivery || "unknown";
      deliveryStats[option] = (deliveryStats[option] || 0) + 1;
    });

    // Payment methods stats
    const paymentStats = {};
    validOrders.forEach(order => {
      const method = order.paymentMethod || "unknown";
      paymentStats[method] = (paymentStats[method] || 0) + 1;
    });

    // Unique customers
    const uniqueCustomers = new Set(
      validOrders.map(order => order.user_id).filter(Boolean)
    ).size;

    return {
      totalRevenue,
      totalOrders,
      averageOrderValue,
      totalItems,
      popularDishes,
      deliveryStats,
      paymentStats,
      uniqueCustomers,
    };
  }, [filteredOrders]);

  const StatCard = ({ title, value, icon: Icon, color, trend, subtitle }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${alpha(
          color,
          0.02
        )} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: alpha(color, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ fontSize: 24, color }} />
          </Box>
          {trend && (
            <Chip
              icon={trend > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${trend > 0 ? "+" : ""}${trend}%`}
              size="small"
              color={trend > 0 ? "success" : "error"}
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const currencySymbol = settings?.currency?.symbol || "$";

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 1,
              color: "text.primary",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            {t("Analytics")} 📊
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t("Track your business performance and insights")}
          </Typography>
        </Box>

        {/* Time Range Selector */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t("Time Range")}</InputLabel>
          <Select
            value={timeRange}
            label={t("Time Range")}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="all">{t("All Time")}</MenuItem>
            <MenuItem value="today">{t("Today")}</MenuItem>
            <MenuItem value="week">{t("Last 7 Days")}</MenuItem>
            <MenuItem value="month">{t("Last 30 Days")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Main Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t("Total Revenue")}
            value={`${currencySymbol}${stats.totalRevenue.toFixed(2)}`}
            icon={AttachMoneyIcon}
            color="#10b981"
            subtitle={`${t("Average")}: ${currencySymbol}${stats.averageOrderValue.toFixed(2)}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t("Total Orders")}
            value={stats.totalOrders}
            icon={ShoppingCartIcon}
            color="#2563eb"
            subtitle={`${stats.totalItems} ${t("items sold")}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t("Unique Customers")}
            value={stats.uniqueCustomers}
            icon={PeopleIcon}
            color="#8b5cf6"
            subtitle={t("Active users")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t("Avg Order Value")}
            value={`${currencySymbol}${stats.averageOrderValue.toFixed(2)}`}
            icon={PaymentIcon}
            color="#f59e0b"
            subtitle={t("Per order")}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Popular Dishes */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                {t("Top Selling Items")} 🔥
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t("Rank")}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t("Dish Name")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t("Quantity Sold")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t("Revenue")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.popularDishes.length > 0 ? (
                      stats.popularDishes.map((dish, index) => (
                        <TableRow
                          key={dish.name}
                          sx={{
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              color={index < 3 ? "primary" : "default"}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {dish.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={dish.quantity}
                              size="small"
                              sx={{ bgcolor: alpha("#10b981", 0.1), color: "#10b981", fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#10b981" }}>
                              {currencySymbol}{dish.revenue.toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" sx={{ color: "text.secondary", py: 4 }}>
                            {t("No sales data available")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Delivery & Payment Stats */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Delivery Options */}
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <LocalShippingIcon sx={{ mr: 1, color: "#2563eb" }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t("Delivery Options")}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {Object.entries(stats.deliveryStats).map(([option, count]) => (
                    <Box key={option}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                          {t(option.replace(/_/g, " "))}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {count}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          bgcolor: alpha("#2563eb", 0.1),
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${(count / stats.totalOrders) * 100}%`,
                            bgcolor: "#2563eb",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PaymentIcon sx={{ mr: 1, color: "#8b5cf6" }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t("Payment Methods")}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {Object.entries(stats.paymentStats).map(([method, count]) => (
                    <Box key={method}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                          {t(method)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {count}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          bgcolor: alpha("#8b5cf6", 0.1),
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${(count / stats.totalOrders) * 100}%`,
                            bgcolor: "#8b5cf6",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
